import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BaseListFilters, ListState, ListStateConfig } from '@/types/list.types';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE,
  DEFAULT_SORT_DIRECTION,
} from '@/constants/list.constants';
import { getActiveFiltersCount } from '@/utils/list.utils';

/**
 * Parse les query params en filters (numérique pour page/limit, string sinon).
 * Note : toutes les keys de l'URL sont reprises dans les filters, pas seulement
 * celles présentes dans defaults — sinon les filtres custom (status, virtualStatus,
 * dates...) ne seraient jamais ré-hydratés au reload depuis l'URL.
 */
function parseFiltersFromUrl<F extends BaseListFilters>(
  searchParams: URLSearchParams,
  defaults: F,
  filterTypes?: Record<string, 'string' | 'number' | 'boolean'>,
): F {
  const result: any = { ...defaults };
  for (const [key, value] of searchParams.entries()) {
    if (key === 'page' || key === 'limit') {
      const num = parseInt(value, 10);
      if (!Number.isNaN(num)) result[key] = num;
    } else {
      const type = filterTypes?.[key];
      if (type === 'number') {
        const num = Number(value);
        if (!Number.isNaN(num)) result[key] = num;
      } else if (type === 'boolean') {
        result[key] = value === 'true';
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Met à jour searchParams avec les filters non-default. Préserve les keys
 * non-gérées par useListState (ex: 'search' géré par useListSearch).
 */
function syncFiltersToUrl<F extends BaseListFilters>(
  filters: F,
  defaults: F,
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  for (const key of Object.keys(filters) as (keyof F)[]) {
    const value = filters[key];
    const defaultValue = defaults[key];
    if (value === undefined || value === null || value === '' || value === defaultValue) {
      next.delete(String(key));
    } else {
      next.set(String(key), String(value));
    }
  }
  return next;
}

/**
 * Hook pour gérer l'état complet d'une liste (filtres, pagination, tri)
 * Conforme au CONTEXT.md : pas de useEffect, dérivation pure via useMemo
 */
export function useListState<F extends BaseListFilters = BaseListFilters>(
  config: ListStateConfig = {},
  defaultFilters?: Partial<F>
): ListState<F> {
  const {
    defaultLimit = DEFAULT_PAGE_SIZE,
    defaultSortBy = 'createdAt',
    defaultSortDirection = DEFAULT_SORT_DIRECTION,
    persistInUrl = false,
    filterTypes,
  } = config;

  // État initial des filtres
  const initialFilters = useMemo(
    () =>
      ({
        page: DEFAULT_PAGE,
        limit: defaultLimit,
        sortBy: defaultSortBy,
        sortDirection: defaultSortDirection,
        ...defaultFilters,
      }) as F,
    [defaultLimit, defaultSortBy, defaultSortDirection, defaultFilters]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFiltersInternal] = useState<F>(() =>
    persistInUrl ? parseFiltersFromUrl(searchParams, initialFilters, filterTypes) : initialFilters,
  );

  // Wrap setFilters pour aussi pousser dans l'URL si persistInUrl
  const setFilters = useCallback<typeof setFiltersInternal>(
    (updater) => {
      setFiltersInternal((prev) => {
        const next =
          typeof updater === 'function' ? (updater as (p: F) => F)(prev) : updater;
        if (persistInUrl) {
          const params = syncFiltersToUrl(next, initialFilters, searchParams);
          setSearchParams(params, { replace: true });
        }
        return next;
      });
    },
    [persistInUrl, initialFilters, searchParams, setSearchParams],
  );

  // Méthode pour mettre à jour un filtre spécifique
  const updateFilter = useCallback(<K extends keyof F>(key: K, value: F[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset à la page 1 si changement de filtre (sauf page/limit)
      ...(key !== 'page' && key !== 'limit' ? { page: DEFAULT_PAGE } : {}),
    }));
  }, [setFilters]);

  // Méthode pour reset tous les filtres
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters, setFilters]);

  // Méthode pour reset uniquement la pagination
  const resetPagination = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: DEFAULT_PAGE,
      limit: defaultLimit,
    }));
  }, [defaultLimit]);

  // Dérivation de la configuration de pagination (useMemo - pas useEffect)
  const pagination = useMemo(
    () => ({
      page: filters.page,
      limit: filters.limit,
    }),
    [filters.page, filters.limit]
  );

  const setPagination = useCallback(
    (config: typeof pagination | ((prev: typeof pagination) => typeof pagination)) => {
      const newConfig = typeof config === 'function' ? config(pagination) : config;
      setFilters((prev) => ({
        ...prev,
        ...newConfig,
      }));
    },
    [pagination]
  );

  // Dérivation de la configuration du tri (useMemo - pas useEffect)
  const sortConfig = useMemo(
    () => ({
      sortBy: filters.sortBy || defaultSortBy,
      sortDirection: filters.sortDirection || defaultSortDirection,
    }),
    [filters.sortBy, filters.sortDirection, defaultSortBy, defaultSortDirection]
  );

  const setSortConfig = useCallback(
    (config: typeof sortConfig | ((prev: typeof sortConfig) => typeof sortConfig)) => {
      const newConfig = typeof config === 'function' ? config(sortConfig) : config;
      setFilters((prev) => ({
        ...prev,
        sortBy: newConfig.sortBy,
        sortDirection: newConfig.sortDirection,
      }));
    },
    [sortConfig]
  );

  // Calcul du nombre de filtres actifs (useMemo - pas useEffect)
  const activeFiltersCount = useMemo(() => {
    return getActiveFiltersCount(filters, initialFilters);
  }, [filters, initialFilters]);

  return {
    filters,
    setFilters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    resetPagination,
    activeFiltersCount,
  };
}
